import { ArmorType } from "../../types/armor";
import { ARMOR_TYPES } from "../../constants/armor";
import { HullConfiguration } from "../../types/hull";

/**
 * Calculates hull cost based on size and configuration
 * @param size - The ship size in tons
 * @param configuration - The hull configuration
 * @returns The hull cost in credits
 */
export const calculateHullCost = (
  size: number,
  configuration: HullConfiguration,
): number => {
  const baseCost = size * (configuration.costPerTon ?? 1);
  const costModifier =
    typeof configuration.costModifier === "number"
      ? configuration.costModifier
      : 1;
  return baseCost * costModifier;
};

/**
 * Calculates hull weight based on size and configuration
 * @param size - The ship size in tons
 * @param configuration - The hull configuration
 * @returns The hull weight in tons
 */
export const calculateHullWeight = (
  size: number,
  configuration: HullConfiguration,
): number => {
  return size * (configuration.weightModifier ?? 1);
};

/**
 * Calculates hull points based on size and configuration
 * @param size - The ship size in tons
 * @param configuration - The hull configuration
 * @returns The hull points
 */
export const calculateHullPoints = (
  size: number,
  configuration: HullConfiguration,
): number => {
  return Math.floor(size / (configuration.hullPointsPerTon ?? 1));
};

/**
 * Calculates armor cost based on type and ship size
 * @param armorType - The armor type
 * @param shipSize - The ship size in tons
 * @returns The armor cost in credits
 */
export const calculateArmorCost = (
  armorType: ArmorType,
  shipSize: number,
  armorRating: number = 1,
  armorVolumeModifier: number = 1,
): number => {
  const armorSpec = ARMOR_TYPES[armorType];
  if (!armorSpec) return 0;

  // Calculate armor tonnage consumed (adjusted for hull type)
  const armorTonnage = shipSize * armorSpec.tonnageConsumed * armorVolumeModifier;

  // Apply size multipliers based on ship size
  let sizeMultiplier = 1;
  if (shipSize >= 5 && shipSize <= 15) sizeMultiplier = 4;
  else if (shipSize >= 16 && shipSize <= 25) sizeMultiplier = 3;
  else if (shipSize >= 26 && shipSize <= 99) sizeMultiplier = 2;
  // 100+ tons uses multiplier = 1

  // Armor cost = Armor Tonnage × Cost Per Ton × Armor Rating × Size Multiplier
  const armorCost = armorTonnage * armorSpec.costPerTon * armorRating * sizeMultiplier;

  return armorCost;
};

/**
 * Calculates armor weight based on type and ship size
 * @param armorType - The armor type
 * @param shipSize - The ship size in tons
 * @returns The armor weight in tons
 */
export const calculateArmorTons = (
  armorType: ArmorType,
  shipSize: number,
): number => {
  const armorSpec = ARMOR_TYPES[armorType];
  if (!armorSpec) return 0;

  // Base weight calculation
  let baseWeight = shipSize * armorSpec.tonnageConsumed;

  // Apply size multipliers
  if (shipSize >= 5 && shipSize <= 15) baseWeight *= 4;
  else if (shipSize >= 16 && shipSize <= 25) baseWeight *= 3;
  else if (shipSize >= 26 && shipSize <= 99) baseWeight *= 2;
  // 100+ tons uses base weight (multiplier = 1)

  return baseWeight;
};

/**
 * Calculates drive cost based on type, rating, and ship size
 * @param driveType - The drive type
 * @param rating - The drive rating
 * @param shipSize - The ship size in tons
 * @returns The drive cost in credits
 */
export const calculateDriveCost = (
  driveType: string,
  rating: number,
  shipSize: number,
): number => {
  const baseCosts: Record<string, number> = {
    M: 0.5, // Maneuver drive
    J: 1.0, // Jump drive
    R: 0.2, // Reaction drive
  };

  const baseCost = baseCosts[driveType] || 0.5;
  return shipSize * baseCost * rating;
};

/**
 * Calculates drive weight based on type, rating, and ship size
 * @param driveType - The drive type
 * @param rating - The drive rating
 * @param shipSize - The ship size in tons
 * @returns The drive weight in tons
 */
export const calculateDriveTons = (
  driveType: string,
  rating: number,
  shipSize: number,
): number => {
  const baseTons: Record<string, number> = {
    M: 0.1, // Maneuver drive - 10% of ship size
    J: 0.1, // Jump drive - 10% of ship size
    R: 0.05, // Reaction drive - 5% of ship size
  };

  const baseWeight = baseTons[driveType] || 0.1;
  return shipSize * baseWeight * rating;
};
